import React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Switch,
    TextField,
    Typography
} from '@mui/material';

export type XeroOverlayCustomerOption = {
    contactId: string;
    name: string;
    customerId?: number;
};

type XeroIntegrationConnectProps = {
    isConnected: boolean;
    isEnabled: boolean;
    isSaving: boolean;
    connectionLabel?: string | null;
    autoDisabledMessage?: string | null;
    overlayMode?: boolean;
    overlayCustomers?: XeroOverlayCustomerOption[];
    selectedCustomerIds?: string[];
    loadingOverlayCustomers?: boolean;
    onConnect: () => void;
    onToggleEnabled?: (checked: boolean) => void;
    onSelectedCustomersChange?: (contactIds: string[]) => void;
    onSaveOverlaySelection?: () => void;
};

export default function XeroIntegrationConnect({
    isConnected,
    isEnabled,
    isSaving,
    connectionLabel,
    autoDisabledMessage,
    overlayMode = false,
    overlayCustomers = [],
    selectedCustomerIds = [],
    loadingOverlayCustomers = false,
    onConnect,
    onToggleEnabled,
    onSelectedCustomersChange,
    onSaveOverlaySelection
}: XeroIntegrationConnectProps) {
    const selectedCustomers = overlayCustomers.filter((customer) =>
        selectedCustomerIds.includes(customer.contactId));

    return (
        <Box sx={{ width: '100%' }}>
            {autoDisabledMessage && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography>Integration was automatically disabled:</Typography>
                    <Typography sx={{ fontWeight: 'bold', my: 1 }}>{autoDisabledMessage}</Typography>
                    <Typography>Enable the integration again after resolving the issue.</Typography>
                </Alert>
            )}

            {overlayMode && (
                <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                    Amazon customers from Xero
                </Typography>
            )}

            {isConnected && onToggleEnabled && (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isEnabled}
                                onChange={(event) => onToggleEnabled(event.target.checked)}
                                color="primary"
                                disabled={isSaving || (overlayMode && selectedCustomerIds.length === 0)}
                            />
                        }
                        label="Enabled"
                    />
                    {isSaving && <CircularProgress size={20} sx={{ ml: 1 }} />}
                </Box>
            )}

            <Alert severity={isConnected ? 'success' : 'info'} sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {isConnected ? 'Xero is connected' : 'Xero is not connected yet'}
                </Typography>
                <Typography>
                    {overlayMode
                        ? (isConnected
                            ? `Connected tenant: ${connectionLabel || 'Unknown tenant'}. Select Amazon customers that already exist in Abakus and have no sales orders.`
                            : 'Authorize AbakusIQ against Xero, then choose Amazon customers that already exist in Abakus.')
                        : (isConnected
                            ? `Connected tenant: ${connectionLabel || 'Unknown tenant'}`
                            : 'Authorize AbakusIQ against Xero using OAuth 2.0 to start syncing customers, products and invoices.')}
                </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', mb: 3 }}>
                <Button variant="contained" onClick={onConnect} disabled={isSaving}>
                    {isConnected ? 'Reconnect to Xero' : 'Connect to Xero'}
                </Button>
                {isSaving && <CircularProgress size={20} />}
            </Box>

            {overlayMode && isConnected && (
                <Box sx={{ mb: 3 }}>
                    <Autocomplete
                        multiple
                        options={overlayCustomers}
                        value={selectedCustomers}
                        loading={loadingOverlayCustomers}
                        disableCloseOnSelect
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.contactId === value.contactId}
                        onChange={(_, value) => onSelectedCustomersChange?.(value.map((customer) => customer.contactId))}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Xero customers"
                                placeholder="Select customers"
                                helperText="Only Amazon-named customers that already exist in Abakus with no sales orders are listed. Leave empty to keep Unleashed-only sync."
                            />
                        )}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="contained"
                            onClick={onSaveOverlaySelection}
                            disabled={isSaving || loadingOverlayCustomers}
                        >
                            Save Xero customers
                        </Button>
                    </Box>
                    {selectedCustomerIds.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No Xero customers selected, so Xero will not sync. Unleashed will continue to sync.
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}
